import React from 'react';
import {
  TypeImplementation,
  Plugins,
  Component,
  Expressions,
  matchExpression,
  getSystem,
  withSystem,
  ExpressionPath,
} from '@packages/systems/wfdl';
import {
  ExpressionEditor,
  updateExpressionAction,
} from '@packages/systems/ExpressionEditor-utils';
import {
  TElement,
  TList,
  EElement,
  EList,
  ERecord,
  TTypeConstructor,
  Expression,
} from '@packages/systems/wfdl-ast';
import {
  EBoolean,
  annotate,
  stripAll,
  getAnn,
} from '@packages/utilities/contextual-expression';
import {
  namesEqual,
  qualify,
  pluginName,
  typeName,
} from '@packages/systems/wfdl-Qualified';
import {generateGuid} from '@packages/utilities/guid';
import {
  identity,
  constantFalse,
  emptyArray,
} from '@packages/utilities/fp/utils';

const qualifiedPaginationCount = qualify(
  Plugins.name('Pagination'),
  Component.name('PaginationCount')
);

// Retrieve the paginationCountPath if it exists
const getPaginationCountPath = (_expr: Expression) =>
  Expressions.findPaths(
    _expr,
    matchExpression({
      Element: (e) => namesEqual(e.type, qualifiedPaginationCount),
      _: constantFalse,
    })
  )[0];

const addPaginationCount = (_expr: Expression) => {
  const paginationChildren = Expressions.getList(_expr);

  if (!paginationChildren) {
    throw Error(
      'Unable to get `paginationChildren` Expression from Pagination'
    );
  }

  return EList([
    ...paginationChildren,
    EElement({
      id: generateGuid(),
      type: qualifiedPaginationCount,
      data: ERecord({children: EList([])}),
    }),
  ]);
};

const removePaginationCount = (_expr: Expression, path: ExpressionPath) =>
  Expressions.updateIn(_expr, path.slice(0, -1), (e) =>
    matchExpression({
      List: (fields) =>
        EList(
          fields.filter((_exprEl) => {
            const element = Expressions.getElement(_exprEl);
            return (
              element && !namesEqual(element.type, qualifiedPaginationCount)
            );
          })
        ),
      _: identity,
    })(e)
  );

const ShowPageCountType = TypeImplementation.Alias({
  type: TList(TElement),
  render: function ShowPageCountRender({expr, dispatch}) {
    const strippedExpr = stripAll(expr);
    const paginationCountPath = getPaginationCountPath(strippedExpr);
    const isPaginationCountVisible = Boolean(paginationCountPath);

    const [checked, setChecked] = React.useState(isPaginationCountVisible);

    React.useEffect(() => {
      if (checked !== isPaginationCountVisible) {
        setChecked(!checked);
      }
    }, [checked, isPaginationCountVisible]);

    const onChange = React.useCallback(() => {
      const updatedChildren = isPaginationCountVisible
        ? // Take it away
          // @ts-expect-error - TS2345 - Argument of type 'ExpressionPath | undefined' is not assignable to parameter of type 'ExpressionPath'.
          removePaginationCount(strippedExpr, paginationCountPath)
        : // Add `PaginationCount` to Pagination's Children
          addPaginationCount(strippedExpr);

      if (updatedChildren) {
        dispatch(updateExpressionAction(updatedChildren));
      }
    }, [dispatch, isPaginationCountVisible, paginationCountPath, strippedExpr]);

    const {type, env} = getAnn(expr);
    const maybeSystem = type ? getSystem(type) : null;
    const system = maybeSystem ? maybeSystem : {};

    const expression = annotate(EBoolean(checked), {
      type: withSystem(
        system,
        TTypeConstructor(
          qualify(pluginName('Basic'), typeName('ReverseLabelBoolean'))
        )
      ),
      env,
      errors: emptyArray,
    });

    return <ExpressionEditor expression={expression} dispatch={onChange} />;
  },
});

export default ShowPageCountType;
